import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  HelpCircle, Briefcase, User, ShoppingCart,
  Search, Tag, ChevronDown, LogOut, Shield, Zap, Disc3, Gauge, FileBadge
} from "lucide-react";

const NAV_LINKS = [
  { to: "/pneumatiques", label: "Pneumatiques", icon: Disc3, testid: "nav-pneumatiques" },
  { to: "/performance", label: "Performance", icon: Gauge, testid: "nav-performance" },
  { to: "/carte-grise", label: "Calcule carte grise", icon: FileBadge, testid: "nav-carte-grise" },
];
import { navCategories } from "../mock";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { isStaff } from "../utils/roles";
import { useSearchSuggest } from "../hooks/useSearchSuggest";
import { SearchSuggestions } from "./SearchSuggestions";

const LOGO_URL = "/logos/ktsauto-logo.png";

const Logo = () => (
  <Link to="/" data-testid="site-logo" className="flex items-center select-none shrink-0 group -my-2">
    <img src={LOGO_URL} alt="KTSauto" className="h-16 sm:h-24 w-auto object-contain drop-shadow-[0_6px_18px_rgba(224,31,31,0.35)] group-hover:drop-shadow-[0_8px_26px_rgba(224,31,31,0.6)] transition-[filter] duration-300" />
  </Link>
);

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { count, total } = useCart();
  const { user, logout } = useAuth();
  const [query, setQuery] = useState("");
  const [mobileNav, setMobileNav] = useState(false);
  const [categories, setCategories] = useState(navCategories);

  useEffect(() => {
    api.get("/categories")
      .then(({ data }) => { if (Array.isArray(data) && data.length) setCategories(data); })
      .catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (open && active >= 0 && active < suggest.items.length) { pick(suggest.items[active]); return; }
    goAll();
  };
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const suggest = useSearchSuggest(query, open);
  useEffect(() => { setActive(-1); }, [suggest.items]);
  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);
  const pick = (p) => { setOpen(false); setQuery(""); navigate(`/produit/${p.id}`); };
  const goAll = () => { if (!query.trim()) return; setOpen(false); navigate(`/recherche?q=${encodeURIComponent(query.trim())}`); };
  const onKey = (e) => {
    if (!open || !suggest.items.length) return;
    const n = suggest.items.length;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % (n + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a <= 0 ? n : a - 1)); }
    else if (e.key === "Escape") setOpen(false);
    else if (e.key === "Enter" && active === n) { e.preventDefault(); goAll(); }
  };

  return (
    <header className="w-full sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-[#040507] border-b border-white/5 text-slate-400 text-[13px]">
        <div className="max-w-[1440px] mx-auto px-5 h-9 flex items-center justify-between">
          <Link to="/aide" data-testid="header-help-link" className="flex items-center gap-1.5 hover:text-white transition-colors"><HelpCircle size={14} className="text-[#FF3B3B]" /> Besoin d'aide&nbsp;?</Link>
          <div className="hidden md:flex items-center gap-1.5 font-semibold uppercase tracking-[0.18em] text-[11px] text-slate-300">
            <Zap size={13} className="text-[#FF3B3B]" /> Expert français de la pièce auto
          </div>
          <Link to="/professionnels" data-testid="header-pro-link" className="hidden sm:flex items-center gap-1.5 hover:text-white transition-colors"><Briefcase size={14} /> Professionnels</Link>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-[#08090C]/95 backdrop-blur-md border-b border-[#1E232D] relative z-[70]">
        <div className="max-w-[1440px] mx-auto px-5 py-3 md:py-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Logo />
          <div className="flex items-center gap-4 sm:gap-6 ml-auto order-2 md:order-3">
            {isStaff(user) && (
              <Link to="/admin" target="_blank" rel="noopener noreferrer" title="Espace administration" data-testid="admin-link" className="flex items-center gap-1.5 bg-[#E01F1F] text-white text-[13px] font-bold uppercase tracking-wide px-2.5 sm:px-3.5 py-2 rounded-lg hover:bg-[#C81818] transition-colors shadow-lg shadow-red-950/40">
                <Shield size={17} /> <span className="hidden sm:inline">Administration</span>
              </Link>
            )}
            {user?.is_pro && (
              <Link to="/espace-pro" target="_blank" rel="noopener noreferrer" title="Espace professionnel" data-testid="header-pro-space-btn" className="flex items-center gap-1.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white text-[13px] font-bold uppercase tracking-wide px-2.5 sm:px-3.5 py-2 rounded-lg transition-colors shadow-lg shadow-emerald-900/30">
                <Briefcase size={17} /> <span className="hidden sm:inline">Espace PRO</span>
              </Link>
            )}
            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/compte" data-testid="header-account-link" className="flex items-center gap-2 group">
                  <User size={24} className="text-slate-200 group-hover:text-[#FF3B3B] transition-colors" strokeWidth={1.6} />
                  <div className="leading-tight hidden sm:block">
                    <div className="font-bold text-slate-50 text-[15px] flex items-center gap-1.5">Mon compte{user.is_pro && <span data-testid="header-pro-badge" className="bg-emerald-950/80 text-emerald-400 border border-emerald-800/60 text-[10px] font-bold px-1.5 py-0.5 rounded">PRO</span>}</div>
                    <div className="text-[13px] text-slate-400 max-w-[120px] truncate">{user.name}</div>
                  </div>
                </Link>
                <button onClick={() => { logout(); navigate("/"); }} title="Se déconnecter" data-testid="header-logout-btn" className="text-slate-500 hover:text-[#FF3B3B] transition-colors">
                  <LogOut size={20} />
                </button>
              </div>
            ) : (
              <Link to="/connexion" data-testid="header-login-link" className="flex items-center gap-2 group">
                <User size={24} className="text-slate-200 group-hover:text-[#FF3B3B] transition-colors" strokeWidth={1.6} />
                <div className="leading-tight hidden sm:block">
                  <div className="font-bold text-slate-50 text-[15px]">Mon compte</div>
                  <div className="text-[13px] text-slate-400">Se connecter</div>
                </div>
              </Link>
            )}
            <Link to="/panier" data-testid="header-cart-link" className="flex items-center gap-2 group">
              <div className="relative">
                <ShoppingCart size={24} className="text-slate-200 group-hover:text-[#FF3B3B] transition-colors" strokeWidth={1.6} />
                <span data-testid="header-cart-count" className="absolute -top-2 -right-2 bg-[#E01F1F] text-white text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow shadow-red-950/50">{count}</span>
              </div>
              <div className="leading-tight hidden sm:block">
                <div className="font-bold text-slate-50 text-[15px]">Mon panier</div>
                <div className="text-[13px] text-slate-400">{total.toFixed(2).replace(".", ",")}&nbsp;€</div>
              </div>
            </Link>
          </div>
          <form onSubmit={submitSearch} className="order-3 w-full md:order-2 md:flex-1 md:max-w-[680px] md:mx-auto relative" role="search">
            <div className="flex items-center bg-[#0D0F14] border border-[#2A3040] rounded-xl overflow-hidden focus-within:border-[#E01F1F] focus-within:shadow-[0_0_0_3px_rgba(224,31,31,0.18)] transition-all">
              <input
                data-testid="header-search-input"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onKeyDown={onKey}
                autoComplete="off"
                aria-autocomplete="list"
                aria-expanded={open && query.trim().length >= 2}
                placeholder="Rechercher une pièce, une référence, une marque..."
                className="flex-1 min-w-0 px-5 py-3 text-[15px] outline-none bg-transparent text-slate-50 placeholder:text-slate-500"
              />
              <button type="submit" data-testid="header-search-btn" className="m-1 px-4 py-2 rounded-lg bg-[#E01F1F] hover:bg-[#C81818] text-white transition-colors shadow-lg shadow-red-950/40">
                <Search size={20} />
              </button>
            </div>
            {open && <SearchSuggestions query={query} items={suggest.items} total={suggest.total} loading={suggest.loading} active={active} onPick={pick} onAll={goAll} onHover={setActive} />}
          </form>
        </div>
      </div>

      {/* Nav bar */}
      <nav className="bg-[#0D0F14] border-b border-[#1E232D] text-white relative" data-testid="main-nav">
        <div className="max-w-[1440px] mx-auto px-5 flex items-center gap-2 sm:gap-4">
          <button
            data-testid="header-categories-btn"
            className={`group shrink-0 flex items-center gap-2.5 font-bold text-[13px] sm:text-[15px] my-2 pl-3 pr-3 sm:pl-4 sm:pr-4 py-2.5 rounded-lg border transition-all duration-200 ${mobileNav ? "bg-[#181C26] border-[#E01F1F] text-white" : "bg-transparent border-[#2A3040] text-slate-100 hover:border-[#E01F1F]/70 hover:bg-[#13161E]"}`}
            onClick={() => setMobileNav(!mobileNav)}
          >
            <span className="relative w-[18px] h-[14px] flex flex-col justify-between">
              <span className={`block h-[2px] w-full bg-[#FF3B3B] rounded transition-transform duration-300 ${mobileNav ? "translate-y-[6px] rotate-45" : ""}`} />
              <span className={`block h-[2px] w-full bg-[#FF3B3B] rounded transition-opacity duration-200 ${mobileNav ? "opacity-0" : ""}`} />
              <span className={`block h-[2px] w-full bg-[#FF3B3B] rounded transition-transform duration-300 ${mobileNav ? "-translate-y-[6px] -rotate-45" : ""}`} />
            </span>
            <span className="whitespace-nowrap"><span className="hidden sm:inline">Toutes nos </span>catégories</span>
            <ChevronDown size={15} className={`text-slate-400 group-hover:text-white transition-transform duration-300 ${mobileNav ? "rotate-180" : ""}`} />
          </button>
          <span className="hidden lg:block w-px h-6 bg-[#2A3040]" />
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map(({ to, label, icon: Icon, testid }) => {
              const active = location.pathname === to || location.pathname.startsWith(to + "/");
              return (
                <Link key={to} to={to} data-testid={testid} aria-current={active ? "page" : undefined}
                  className={`group relative flex items-center gap-2 px-3.5 py-4 text-[14px] font-semibold uppercase tracking-wide transition-colors ${active ? "text-white" : "text-slate-400 hover:text-white"}`}>
                  <Icon size={16} className={`transition-colors ${active ? "text-[#FF3B3B]" : "text-slate-500 group-hover:text-[#FF3B3B]"}`} />
                  {label}
                  <span className={`absolute left-3.5 right-3.5 bottom-0 h-[3px] rounded-t bg-[#E01F1F] origin-left transition-transform duration-300 ${active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                </Link>
              );
            })}
          </div>
          <Link to="/categorie/promotions" data-testid="nav-promotions" className="ml-auto shrink-0 group flex items-center gap-1.5 sm:gap-2 text-[#FF3B3B] font-extrabold italic uppercase tracking-tight text-[12px] sm:text-[14px] my-2 px-2.5 sm:px-3.5 py-2 rounded-lg border border-[#3a1414] bg-[#160a0a] hover:bg-[#E01F1F] hover:text-white hover:border-[#E01F1F] transition-all duration-200">
            <Tag size={15} className="group-hover:rotate-[-12deg] transition-transform" /> <span className="hidden min-[400px]:inline">Promotions</span><span className="min-[400px]:hidden">Promos</span>
          </Link>
        </div>
        {mobileNav && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setMobileNav(false)} />
            <div className="absolute left-0 right-0 top-full bg-[#10131A] text-slate-50 shadow-2xl shadow-black/60 z-40 border-t-2 border-[#E01F1F]">
              <div className="max-w-[1440px] mx-auto px-5 py-5">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/categorie/${c.slug}`}
                      onClick={() => setMobileNav(false)}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg text-[15px] font-medium text-slate-300 hover:bg-[#181C26] hover:text-white transition-colors"
                    >
                      {c.name} <ChevronDown size={15} className="-rotate-90 text-slate-500" />
                    </Link>
                  ))}
                </div>
                <Link
                  to="/vehicule"
                  onClick={() => setMobileNav(false)}
                  className="mt-3 flex md:hidden items-center gap-2 px-3 py-2.5 rounded-lg text-[15px] font-semibold text-slate-50 bg-[#181C26] hover:text-[#FF3B3B] transition-colors"
                >
                  Recherche par véhicule
                </Link>
                <div className="mt-3 flex flex-wrap gap-2 border-t border-[#1E232D] pt-3 lg:hidden">
                  <Link to="/pneumatiques" onClick={() => setMobileNav(false)} className="px-3 py-2 rounded-lg text-[14px] font-semibold text-slate-50 bg-[#181C26] hover:text-[#FF3B3B] transition-colors">Pneumatiques</Link>
                  <Link to="/performance" data-testid="mobile-nav-performance" onClick={() => setMobileNav(false)} className="px-3 py-2 rounded-lg text-[14px] font-semibold text-slate-50 bg-[#181C26] hover:text-[#FF3B3B] transition-colors">Performance</Link>
                  <Link to="/carte-grise" onClick={() => setMobileNav(false)} className="px-3 py-2 rounded-lg text-[14px] font-semibold text-slate-50 bg-[#181C26] hover:text-[#FF3B3B] transition-colors">Calcule Carte Grise</Link>
                </div>
              </div>
            </div>
          </>
        )}
      </nav>
    </header>
  );
};

export default Header;
